import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";

export default function Hero() {
  const { scrollY } = useScroll();

  const bgY = useTransform(scrollY, [0, 800], [0, 200]);
  const bgScale = useTransform(scrollY, [0, 800], [1.08, 1.02]);
  const contentOpacity = useTransform(scrollY, [0, 300], [1, 0.3]);
  const contentY = useTransform(scrollY, [0, 300], [0, -30]);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Parallax */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        style={{ y: bgY, scale: bgScale }}
      >
        <img
          src="https://storage.readdy-site.link/project_files/e2268d4a-73e3-4367-9d65-df24b53a0559/dc3213e2-dacf-4322-b387-e6b59204122c_compressed_sanduche_em_embalagem_colorida_ao_sol.webp"
          alt="Sanduíches naturais premium Big Mania"
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/35 via-55% to-foreground-950/75"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/40 via-transparent via-50% to-foreground-950/40"></div>

      {/* Content */}
      <motion.div
        className="relative z-10 w-full px-4 sm:px-6 md:px-10 pt-20 pb-16 flex flex-col items-center text-center"
        style={{ opacity: contentOpacity, y: contentY }}
      >
        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-background-50 max-w-4xl leading-[1.08] mb-6"
        >
          O sabor natural que
          <br />
          <span className="gradient-text-warm">combina</span> com todos os momentos
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-base sm:text-lg md:text-xl text-background-100/85 max-w-2xl leading-relaxed mb-10"
        >
          Sanduíches naturais artesanais, alimentos congelados premium e soluções completas para
          varejo, atacado, empresas e eventos em toda Fortaleza.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto"
        >
          <motion.a
            href="https://wa.me/558534942701"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="group w-full sm:w-auto flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl bg-primary-500 text-background-50 text-base font-semibold transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Comprar pelo WhatsApp
            <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
          </motion.a>

          <Link
            to="/cardapio"
            className="group w-full sm:w-auto flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl bg-background-50/8 backdrop-blur-sm border border-background-50/20 text-background-50 text-base font-semibold hover:bg-background-50/15 hover:border-background-50/35 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-restaurant-line text-lg"></i>
            Ver Cardápio
          </Link>

          <motion.button
            onClick={() => scrollTo("#eventos")}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="group w-full sm:w-auto flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl bg-accent-500/80 backdrop-blur-sm text-background-50 text-base font-semibold hover:bg-accent-500 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-event-line text-lg"></i>
            Orçamento Eventos
          </motion.button>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-14 flex flex-wrap items-center justify-center gap-5 sm:gap-8 text-background-100/65 text-xs sm:text-sm"
        >
          <span className="flex items-center gap-2">
            <i className="ri-shield-check-line text-accent-400 text-sm"></i>
            Segurança Alimentar
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-truck-line text-accent-400 text-sm"></i>
            Entrega em Fortaleza e Regiões Metropolitanas
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-award-line text-accent-400 text-sm"></i>
            20+ Anos de Experiência
          </span>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-background-100/50"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <span className="text-[10px] tracking-[0.22em] uppercase">Rolar</span>
        <i className="ri-arrow-down-line text-lg"></i>
      </motion.div>
    </section>
  );
}